/**
 * Transactions Service (admin scope)
 *
 * The in-app payment flow has been removed for launch — deals are settled
 * offline between buyer and seller. What remains here is the admin surface
 * over historical/legacy transactions: dispute handling and open-transaction
 * management (AdminModerationScreen, AdminTransactionsScreen).
 */

import { supabase } from '../config/supabase';
import { Transaction } from '../types';

export const transactionsService = {
  /**
   * Resolve a dispute (admin only)
   * @param transactionId Transaction ID
   * @param action 'approve' to release payment to seller, 'refund' to refund buyer
   */
  async resolveDispute(
    transactionId: string,
    action: 'approve' | 'refund'
  ): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Not authenticated');

    // Get transaction
    const { data: transaction, error: txError } = await supabase
      .from('transactions')
      .select('*')
      .eq('id', transactionId)
      .single();

    if (txError) throw txError;
    if (!transaction) throw new Error('Transaction not found');

    if (transaction.status !== 'disputed') {
      throw new Error('Transaction is not disputed');
    }

    if (action === 'approve') {
      // Approve payment - release to seller
      const { error } = await supabase
        .from('transactions')
        .update({
          status: 'paid',
          dispute_resolved_at: new Date().toISOString(),
        })
        .eq('id', transactionId);

      if (error) throw error;
    } else if (action === 'refund') {
      // Issue refund to buyer
      await this.refundTransaction(transactionId);

      // Update dispute resolution timestamp
      const { error } = await supabase
        .from('transactions')
        .update({
          dispute_resolved_at: new Date().toISOString(),
        })
        .eq('id', transactionId);

      if (error) throw error;
    } else {
      throw new Error('Invalid action');
    }
  },

  /**
   * Get all disputed transactions (admin only)
   * @returns List of disputed transactions
   */
  async getDisputedTransactions(): Promise<Transaction[]> {
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        conversation:conversations(*),
        item:clothing_items(*, club:clubs(*)),
        buyer:profiles!transactions_buyer_id_fkey(*),
        seller:profiles!transactions_seller_id_fkey(*)
      `)
      .eq('status', 'disputed')
      .order('dispute_created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Mark a transaction as refunded (admin only).
   *
   * With the in-app payment flow removed there is no payment provider to move
   * money through: any actual repayment happens offline between the parties.
   * This only records the outcome on the transaction row.
   */
  async refundTransaction(transactionId: string): Promise<void> {
    const { data: transaction, error: txError } = await supabase
      .from('transactions')
      .select('id')
      .eq('id', transactionId)
      .single();

    if (txError) throw txError;
    if (!transaction) throw new Error('Transaction not found');

    const { error } = await supabase
      .from('transactions')
      .update({
        status: 'refunded',
        refunded_at: new Date().toISOString(),
      })
      .eq('id', transactionId);

    if (error) throw error;
  },

  /**
   * Get all open transactions (admin only).
   * "Open" = not yet completed, cancelled, failed, or refunded.
   */
  async getAllOpenTransactions(): Promise<Transaction[]> {
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        item:clothing_items(*, club:clubs(*)),
        buyer:profiles!transactions_buyer_id_fkey(*),
        seller:profiles!transactions_seller_id_fkey(*)
      `)
      .in('status', ['pending', 'buyer_confirmed', 'paid', 'disputed'])
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Update a transaction status as admin (cancel or reset to pending).
   *
   * Refuses to act on `paid` transactions: doing so would orphan the
   * seller_payouts row created by the create_seller_payout_on_payment
   * trigger. Use `refundTransaction` to unwind a paid transaction.
   */
  async adminUpdateTransactionStatus(
    transactionId: string,
    status: 'cancelled' | 'pending'
  ): Promise<void> {
    const { data: current, error: fetchError } = await supabase
      .from('transactions')
      .select('status')
      .eq('id', transactionId)
      .single();

    if (fetchError) throw fetchError;
    if (!current) throw new Error('Transaction not found');

    if (current.status === 'paid') {
      throw new Error(
        'Cannot cancel or reset a paid transaction directly. Use the refund flow.'
      );
    }

    const { error } = await supabase
      .from('transactions')
      .update({ status })
      .eq('id', transactionId);

    if (error) throw error;
  },
};
